import { FlexLayout, H4, Icon, Input } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { ToggleSwitch, Tooltip } from 'components';
import _ from 'lodash';
import type { FC } from 'react';
import { useRef, useState } from 'react';

interface INativeInputControlProps {
  isEditable: boolean;
  isHeaderHidden?: boolean;
  property: string;
  controlType: string;
  value: string | number | boolean | undefined | readonly string[];
  notEmpty: boolean;
  required: boolean;
  description?: string;
  onValueChange: (key: string, value: string | number | boolean | undefined | readonly string[]) => void;
  onBlur?: (key: string, value: string | number | boolean | undefined | readonly string[]) => void;
  isPassword?: boolean;
}

const NativeInputControl: FC<INativeInputControlProps> = ({
  isEditable,
  property,
  controlType,
  value,
  notEmpty,
  required,
  description,
  onValueChange,
  isHeaderHidden,
  isPassword = false,
  onBlur
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const validateField = () => {
    if (_.isUndefined(value) && required) {
      if (inputRef.current?.value && inputRef.current?.value.length) {
        if (inputRef.current) {
          /**
           * QuickFix, Got old input values while input was empty
           */
          inputRef.current.value = '';
        }
      }

      return 'Required to be filled';
    }
    if (_.isEmpty(value) && !(typeof value === 'number') && notEmpty) {
      return `Must have a value "${value}"`;
    }
  };

  const cssProps: IPropertyBag = {
    opacity: validateField() && !isFocused ? 1 : 0,
    transition: 'opacity 0.3s ease !important'
  };

  return (
    <FlexLayout alignItems="center" width="full">
      {isHeaderHidden || (
        <FlexLayout width="full" alignItems="center" justifyContent="space-between">
          <H4 fontWeight="bold" mr="xs" style={{ textTransform: 'capitalize' }}>
            {property}
          </H4>
          {description && (
            <Tooltip content={description} direction="left">
              <Icon src="helpOutline" color="secondary" stroke="transparent" />
            </Tooltip>
          )}
        </FlexLayout>
      )}
      {controlType === 'boolean' && (
        <ToggleSwitch
          defaultValue={value as boolean}
          onChange={val => onValueChange(property, val)}
          disabled={!isEditable}
        />
      )}
      {(controlType === 'string' || controlType === 'number') && (
        <FlexLayout css={{ position: 'relative' }} width="full">
          <Input
            css={isPassword ? { WebkitTextSecurity: 'disc', TextSecurity: 'disc' } : {}}
            width="full"
            ref={inputRef}
            mt="xs"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            type={controlType}
            borderColor={validateField() ? 'error' : 'transparent'}
            disabled={!isEditable}
            defaultValue={value as string | number | undefined | readonly string[]}
            onChange={e => {
              if (inputRef.current) {
                let inputValue: number | string | undefined = inputRef.current.value;
                if (controlType === 'number') {
                  inputValue = isNaN(parseFloat(inputValue)) ? undefined : parseFloat(inputValue);
                }
                onValueChange(property, inputValue);
              }
            }}
            onBlurCapture={e => {
              if (inputRef.current && onBlur) {
                let inputValue: number | string | undefined = inputRef.current.value;
                if (controlType === 'number') {
                  inputValue = isNaN(parseFloat(inputValue)) ? undefined : parseFloat(inputValue);
                }
                onBlur(property, inputValue);
              }
            }}
          />
          <Icon
            src="errorOutlineRounded"
            color="error"
            stroke="transparent"
            css={{
              position: 'absolute',
              right: 10,
              top: 22,
              ...cssProps
            }}
          />

          <H4
            color="error"
            fontSize="md"
            onClick={() => inputRef.current?.focus()}
            css={{
              top: 24,
              left: 10,
              position: 'absolute',
              ...cssProps
            }}
          >
            {validateField()}
          </H4>
        </FlexLayout>
      )}
    </FlexLayout>
  );
};

export default NativeInputControl;
